import { useLocation } from "@reach/router"
import * as React from "react"

type Options = {
  buttonIds: string[] // e.g. ["desktopTermlyButton", "tabletTermlyButton", "mobileTermlyButton"]
  match?: "all" | "any" // default "all" (require all IDs), set "any" if only one exists per breakpoint
  scriptId?: string // default "termlyScript"
  scriptUrl: string
  timeoutMs?: number // optional: give up waiting and inject anyway
}

export function useTermlyConsent({
  buttonIds,
  match = "all",
  scriptId = "termlyScript",
  scriptUrl,
  timeoutMs = 10000,
}: Options) {
  const location = useLocation()
  const [buttonsReady, setButtonsReady] = React.useState(false)
  const [scriptReady, setScriptReady] = React.useState(false)
  const firstLoadRef = React.useRef(true)

  // Reset on route change (skip initial mount)
  React.useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false
      return
    }
    setButtonsReady(false)
    setScriptReady(false)
    cleanupScript(scriptId)
  }, [location.pathname, scriptId])

  // Wait for buttons to exist (no polling)
  React.useEffect(() => {
    if (typeof document === "undefined") return
    const { disconnect, done } = waitForElements(buttonIds, match, timeoutMs)
    let cancelled = false
    done.then(() => {
      if (!cancelled) setButtonsReady(true)
    })
    return () => {
      cancelled = true
      disconnect()
    }
    // join to avoid array reference changes re-running effect
  }, [buttonIds.join("|"), match, timeoutMs])

  // Inject Termly once buttons are ready
  React.useEffect(() => {
    if (!buttonsReady || scriptReady) return
    let mounted = true
    injectScriptOnce(scriptUrl, scriptId)
      .then(() => {
        if (mounted) setScriptReady(true)
      })
      .catch((e) => console.warn("Termly failed to load:", e))
    return () => {
      mounted = false
    }
  }, [buttonsReady, scriptReady, scriptUrl, scriptId])
}

function cleanupScript(scriptId: string) {
  if (typeof document === "undefined") return
  const script = document.getElementById(scriptId)
  if (script) {
    // Reset Termly’s internal flag so it re-initializes after re-injection
    ;(window as any).TERMLY_RESOURCE_BLOCKER_LOADED = undefined
    script.remove()
  }
}

function getEl(id: string) {
  return typeof document !== "undefined" ? document.getElementById(id) : null
}

function injectScriptOnce(url: string, scriptId: string) {
  return new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") return resolve()

    const existing = document.getElementById(
      scriptId
    ) as HTMLScriptElement | null
    if (existing) {
      if ((existing as any)._loaded) return resolve()
      existing.addEventListener("load", () => resolve())
      existing.addEventListener("error", (e) => reject(e))
      return
    }

    const script = document.createElement("script")
    script.id = scriptId
    script.src = url
    script.type = "text/javascript"
    script.async = true
    ;(script as any)._loaded = false

    script.addEventListener("load", () => {
      ;(script as any)._loaded = true
      resolve()
    })
    script.addEventListener("error", (e) => reject(e))

    document.body.appendChild(script)
  })
}

function waitForElements(
  buttonIds: string[],
  mode: "all" | "any",
  timeoutMs = 10000
) {
  let resolve!: (value: boolean) => void
  const done = new Promise<boolean>((res) => (resolve = res))

  const isReady = () =>
    mode === "all"
      ? buttonIds.every((id) => !!getEl(id))
      : buttonIds.some((id) => !!getEl(id))

  if (isReady()) {
    resolve(true)
    return { disconnect: () => {}, done }
  }

  const observer = new MutationObserver(() => {
    if (isReady()) {
      observer.disconnect()
      resolve(true)
    }
  })

  observer.observe(document.documentElement, { childList: true, subtree: true })

  const timeoutId =
    timeoutMs > 0
      ? window.setTimeout(() => {
          observer.disconnect()
          resolve(false) // timed out; caller may inject anyway
        }, timeoutMs)
      : null

  const disconnect = () => {
    observer.disconnect()
    if (timeoutId !== null) window.clearTimeout(timeoutId)
  }

  return { disconnect, done }
}
